import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { BiArrowBack } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import { Backdrop, CircularProgress } from "@mui/material";
import AdminDashboardLayout from "../../layout/AdminDashboardLayout";
import { StyledContainer } from "../../globalStyle/GlobalStyle";
import { PageHeader, SectionWrapper } from "./Apprenticeship.styled";
import FormAccordion from "../../components/Form/FormAccordion";
import { InputField, SelectField } from "../../components/Form/FormComponents";
import { Formgroup } from "../Products/Forms/ProductForm.styled";
import useParameters from "../../utils/useParameters";
import { DurationOptions, adminCreateQuickFinanceReq, businessTypes } from "./utils";
import { CustomSmallBtn } from "../lease/Lease.styled";
import Parse from "../../api/ApiConfig";

const CreateFinanceRequests = () => {
	const navigate = useNavigate();

	const parameters = useParameters();

	const afInterest = parameters?.find(
		(param) => param.parameter === "Base Interest rate Apprenticeship Finance"
	);

	const [createRequestLoading, setCreateRequestLoading] = useState(false);

	const [formData, setFormData] = useState({
		businessType: "",
		amountToInvest: "",
		expectedProfit: "",
		waitingPeriod: "",
		duration: "",
		afType: "Quick Finance",
		interest: "0",
	});

	const handleInputChange = (e) => {
		setFormData({ ...formData, [e.target.name]: e.target.value });
	};

	const handleBackButton = () => {
		navigate(-1);
	};

	const User = Parse.Object.extend("_User");
	const user = new User();
	user.id = Parse.User.current()?.id;

	const handleProceed = async () => {
		if (formData?.businessType === "") {
			toast.error("Please select your business interest");
		} else if (formData?.amountToInvest === "") {
			toast.error("Please input an amount to invest");
		} else if (formData?.expectedProfit === "") {
			toast.error("Please state your expected returns");
		} else if (formData?.waitingPeriod === "" || formData?.duration === "") {
			toast.error("Please select both waiting and AF period");
		} else {
			const investorData = {
				name: "Fortcore Admin",
				email: user?.get("email"),
				phone: user?.get("phone"),
				address: user?.get("homeAddress"),
				userId: user,
			};

			const data = {
				user: user.toPointer(),
				amountToInvest: formData?.amountToInvest,
				duration: formData?.duration,
				waitingPeriod: formData?.waitingPeriod,
				amountToRepay: formData?.expectedProfit,
				interest: formData?.interest,
				businessType: formData?.businessType,
				status: "inactive",
				type: "quick",
				requestType: "admin-creates",
				interestRate: afInterest?.value,
				investorInfo: investorData,
				apprentice: [],
			};

			setCreateRequestLoading(true);
			try {
				const response = await adminCreateQuickFinanceReq(data);

				if (response) {
					toast.success("Request created successfully. Proceed to activate.");
					setTimeout(() => {
						setCreateRequestLoading(false);
						navigate("/apprenticeship/created_assets/?filter=quick_finance");
					}, 2000);
				} else {
					throw "Failed to create request.";
				}
			} catch (error) {
				toast.error(error);
				setCreateRequestLoading(false);
			}
		}
	};

	useEffect(() => {
		const calculateInterestAmount = () => {
			if (afInterest?.value != null && formData.amountToInvest !== "") {
				const interestRate = parseFloat(afInterest.value.replace(/%/g, ""));
				const amount = parseFloat(formData.amountToInvest.replace(/,/g, ""));

				if (!isNaN(interestRate) && !isNaN(amount)) {
					const calculatedInterestRate = (amount * interestRate) / 100;

					const formattedInterestRate = calculatedInterestRate.toLocaleString(undefined, {
						minimumFractionDigits: 2,
						maximumFractionDigits: 2,
					});

					setFormData((prevState) => ({
						...prevState,
						interest: formattedInterestRate,
					}));
				}
			}
		};

		calculateInterestAmount();
	}, [formData?.amountToInvest, afInterest?.value]);

	return (
		<AdminDashboardLayout>
			<Backdrop
				sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
				open={createRequestLoading}>
				<CircularProgress color='inherit' />
			</Backdrop>

			<StyledContainer>
				<PageHeader>
					<div className='topHeader flex'>
						<button
							onClick={handleBackButton}
							style={{ color: "#006770" }}
							className='flex gap-2 border px-3'>
							<BiArrowBack size={24} />
							<p>Go back</p>
						</button>
					</div>
					<SectionWrapper>
						<h3>Create Apprentice Finance</h3>
						<FormAccordion title='Request Information' open={true}>
							<div className='grid grid-cols-3 gap-5 mt-3'>
								<Formgroup>
									<label>BUSINESS INTEREST</label>
									<SelectField
										name='businessType'
										handleChange={handleInputChange}
										isEditable={true}
										value={formData?.businessType}
										options={businessTypes?.map((a) => a?.label)}
									/>
								</Formgroup>

								<Formgroup>
									<label>A.F TYPE</label>
									<InputField
										name='afType'
										handleChange={handleInputChange}
										isEditable={false}
										value={formData?.afType}
									/>
								</Formgroup>

								<Formgroup>
									<label>AMOUNT TO INVEST</label>
									<InputField
										name='amountToInvest'
										handleChange={handleInputChange}
										isEditable={true}
										value={formData?.amountToInvest}
									/>
								</Formgroup>

								<Formgroup>
									<label>EXPECTED PROFIT</label>
									<InputField
										name='expectedProfit'
										handleChange={handleInputChange}
										isEditable={true}
										value={formData?.expectedProfit}
									/>
								</Formgroup>

								<Formgroup>
									<label>WAITING PERIOD</label>
									<SelectField
										name='waitingPeriod'
										handleChange={handleInputChange}
										isEditable={true}
										value={formData?.waitingPeriod}
										options={DurationOptions}
									/>
								</Formgroup>

								<Formgroup>
									<label>A.F DURATION</label>
									<SelectField
										name='duration'
										handleChange={handleInputChange}
										isEditable={true}
										value={formData?.duration}
										options={DurationOptions}
									/>
								</Formgroup>

								<Formgroup>
									<label>INTEREST RATE</label>
									<InputField
										name='interestRate'
										handleChange={handleInputChange}
										isEditable={false}
										value={afInterest?.value}
									/>
								</Formgroup>

								<Formgroup>
									<label>INTEREST</label>
									<InputField
										name='interest'
										handleChange={handleInputChange}
										isEditable={false}
										value={`NGN ${formData?.interest} (${afInterest?.value} / PA)`}
									/>
								</Formgroup>

								<div className='form-group' style={{ gridColumn: "3/4", gridRow: "4/5", justifySelf: "end" }}>
									<CustomSmallBtn
										style={{ height: "45px", color: "white", background: "#006770" }}
										type='button'
										onClick={handleProceed}>
										Create Finance Request
									</CustomSmallBtn>
								</div>
							</div>
						</FormAccordion>
					</SectionWrapper>
				</PageHeader>
			</StyledContainer>
		</AdminDashboardLayout>
	);
};

CreateFinanceRequests.propTypes = {};

export default CreateFinanceRequests;
